/**
 * AdventureBanner Component
 * 
 * Displays an adventure banner with WebP/JPEG fallback support and lazy loading.
 * Used in scene headers during gameplay alongside character portraits.
 * Aspect ratio: 5:1 (ultra-wide panoramic)
 */

import React, { useState, useEffect } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageVariantSize } from '../../services/imageService';
import { resolveAdventureBannerId } from '../../utils/adventureTitleImageId';
import useGameStore from '../../stores/gameStore';
import { usePictureImageLoad } from '../../hooks/usePictureImageLoad';
import PictureImageContent from './PictureImageContent';
import LoadingSpinner from './LoadingSpinner';

export interface AdventureBannerProps {
  /**
   * Banner ID to display (e.g., 'fantasy', 'mystery', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  bannerId?: string | undefined;

  /**
   * Adventure ID for resolving unqualified banner IDs to adventure-specific
   * namespaced IDs (e.g. template_event → events/template/host:template_event).
   */
  adventureId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 320×64px (for scene headers)
   * - card: 640×128px (for medium displays)
   * - large: 1280×256px (for large displays)
   */
  variant?: ImageVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;

  /**
   * When true, the banner fills its container (width and height 100%).
   * Use in a fixed-height row (e.g. scene header) so banner matches portrait height;
   * sides may be trimmed via object-fit cover.
   */
  fillContainer?: boolean;
}

/**
 * Default fallback component when banner cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: ImageVariantSize; alt: string | undefined }> = ({ variant, alt }) => {
  const sizes = {
    thumbnail: 'w-80 h-16',
    card: 'w-[40rem] h-32',
    large: 'w-[80rem] h-64',
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}
      role="img"
      aria-label={alt || 'Adventure banner placeholder'}
    >
      <span className="text-2xl">━━━━━━━━━━━</span>
    </div>
  );
};

const BANNER_SIZE_CLASSES = {
  thumbnail: 'w-80 h-16',
  card: 'w-[40rem] h-32',
  large: 'w-[80rem] h-64',
} as const;

/**
 * AdventureBanner component
 */
export const AdventureBanner: React.FC<AdventureBannerProps> = ({
  bannerId,
  adventureId,
  variant = 'thumbnail',
  alt,
  className = '',
  style,
  fallback,
  lazy = true,
  fillContainer = false,
}) => {
  const sessionAdventureId = useGameStore(
    (state) =>
      state.currentAdventure?.id
      ?? state.selectedAdventureId
      ?? state.currentSession?.adventureId
      ?? undefined
  );
  const effectiveAdventureId = adventureId ?? sessionAdventureId;

  const [resolvedBannerId, setResolvedBannerId] = useState<string | undefined>(undefined);
  const [resolveReady, setResolveReady] = useState(!effectiveAdventureId);

  useEffect(() => {
    let cancelled = false;

    const resolveId = async () => {
      if (!effectiveAdventureId) {
        if (!cancelled) {
          setResolvedBannerId(bannerId);
          setResolveReady(true);
        }
        return;
      }

      if (!cancelled) {
        setResolveReady(false);
      }

      try {
        await imageService.loadAvailableIds();
      } catch {
        // Resolver falls back to header value when IDs are unavailable
      }

      if (!cancelled) {
        setResolvedBannerId(
          resolveAdventureBannerId(
            effectiveAdventureId,
            bannerId,
            imageService.getBannerIds()
          )
        );
        setResolveReady(true);
      }
    };

    void resolveId();

    return () => {
      cancelled = true;
    };
  }, [effectiveAdventureId, bannerId]);

  const activeBannerId =
    resolveReady && resolvedBannerId !== undefined ? resolvedBannerId : undefined;

  const baseUrls = activeBannerId
    ? imageService.getBannerUrls(activeBannerId, variant)
    : null;
  const identityKey = `${activeBannerId ?? 'pending'}-${variant}`;

  const {
    imgRef,
    isLoading,
    failedUrl,
    imageUrls,
    pictureKey,
    handleLoad,
    handleError,
  } = usePictureImageLoad(baseUrls, identityKey, {
    enabled: Boolean(activeBannerId),
    lazy,
    onLoadFailed: () => {
      console.warn(`Failed to load banner: ${activeBannerId}`);
    },
  });

  const containerSizeClass = fillContainer ? 'w-full h-full' : BANNER_SIZE_CLASSES[variant];
  const fillStyle = fillContainer ? { width: '100%', height: '100%', minHeight: 0 } : undefined;

  if (!activeBannerId) {
    return (
      <div
        className={`${containerSizeClass} relative ${className}`}
        style={{
          borderRadius: '12px',
          border: '1px solid #000000',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          backgroundColor: 'transparent',
          ...fillStyle,
          ...style,
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700" aria-busy="true" aria-label="Loading image">
          <LoadingSpinner size="xs" color="white" />
        </div>
      </div>
    );
  }

  if (!imageUrls || failedUrl === imageUrls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }

  return (
    <div 
      className={`${containerSizeClass} relative ${className}`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...fillStyle,
        ...style
      }}
    >
      <PictureImageContent
        imageUrls={imageUrls}
        pictureKey={pictureKey}
        imgRef={imgRef}
        alt={alt || 'Adventure banner'}
        lazy={lazy}
        isLoading={isLoading}
        onLoad={handleLoad}
        onError={handleError}
        spinnerSize="xs"
      />
    </div>
  );
};

export default AdventureBanner;
